import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, Capabilities } from '@/api/client'
import { usePageLoader } from '@/components/LogoLoader'
import { useMe } from '@/features/auth/useAuth'
import { fmtDateTime, fmtNumber, useT } from '@/i18n'
import { inventoryMessages } from '@/i18n/messages/inventory'
import { normalizeDrumCode } from './drumCode'
import { Select } from '@/components/Select'
import { Dialog, Field, GhostButton, INPUT, LeftBar, PrimaryButton, StatusBadge } from './shared'
import type { CableChoice, CrewChoice, Drum, DrumDetail, Location, Movement, ProjectChoice } from './types'

type Action = 'transfer' | 'issue' | 'return' | 'count' | 'write_off'

/**
 * The page a drum's QR opens (/d/MIH-XXXXXXC). Built for a phone in a trench: what is on the
 * drum, where it is, and one big button per thing that can happen to it next.
 */
export function DrumPage() {
  const t = useT(inventoryMessages)
  const { code: raw } = useParams<{ code: string }>()
  const code = normalizeDrumCode(raw)
  const { data: me } = useMe()
  const canMove = me?.capabilities.includes(Capabilities.MoveInventory) ?? false
  const [action, setAction] = useState<Action | null>(null)

  const { data, isPending, error } = useQuery<DrumDetail>({
    queryKey: ['inv-drum', code],
    queryFn: () => api.get<DrumDetail>(`/api/inventory/drums/${code}`),
    enabled: Boolean(code),
    retry: false,
  })

  const loader = usePageLoader(Boolean(code) && isPending, t('loadingDrum'))
  if (loader) return loader
  if (!code || error || !data) return (
    <div className="mx-auto max-w-md space-y-3 py-8 text-center">
      <p className="font-mono text-lg font-semibold">{raw}</p>
      <p className="text-sm text-neutral-600 dark:text-neutral-300">{t('drumNotFound')}</p>
      <Link to="/inventory" className="text-sm text-brand-700 hover:underline dark:text-brand-200">← {t('title')}</Link>
    </div>
  )

  const d = data.drum
  if (d.status === 'unregistered') {
    return (
      <div className="mx-auto max-w-lg space-y-4">
        <Back />
        <h1 className="font-mono text-2xl font-bold">{d.code}</h1>
        {canMove ? <RegisterForm drum={d} /> : <p className="text-sm text-neutral-600 dark:text-neutral-300">{t('unregisteredNoRight')}</p>}
      </div>
    )
  }

  const actions: Action[] = d.status === 'written_off' ? []
    : d.status === 'in_stock' ? ['transfer', 'issue', 'count', 'write_off']
    : d.status === 'at_site' ? ['issue', 'return', 'transfer', 'count', 'write_off']
    : ['return', 'count', 'write_off']
  const label: Record<Action, string> = {
    transfer: t('actTransfer'), issue: t('actIssue'), return: t('actReturn'), count: t('actCount'), write_off: t('actWriteOff'),
  }

  return (
    <div className="mx-auto max-w-lg space-y-4">
      <Back />
      <header className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="font-mono text-2xl font-bold">{d.code}</h1>
          <StatusBadge status={d.status} />
        </div>
        <p className="text-lg font-medium leading-snug">{d.cableName}</p>
        {d.provider && <p className="text-sm text-neutral-500">{t('provider')}: {d.provider}</p>}
      </header>

      <div className="space-y-3 rounded-2xl bg-white p-4 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
        <div>
          <div className="mb-1 flex justify-between text-xs text-neutral-500">
            <span>{t('left')}</span><span>{t('delivered')} {fmtNumber(d.lengthDeliveredM ?? 0)} m</span>
          </div>
          <LeftBar drum={d} wide />
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <Info label={t('where')} value={d.locationName} />
          <Info label={t('holder')} value={d.kolonaName} />
          <Info label={t('project')} value={d.projectName} />
          {d.supplierRef && <Info label={t('deliveryNote')} value={d.supplierRef} />}
        </dl>
      </div>

      {d.status === 'written_off' ? (
        <p className="rounded-xl bg-neutral-100 p-3 text-sm text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">{t('writtenOffNote')}</p>
      ) : canMove && (
        <div className="grid gap-2">
          {actions.map((a, i) => (
            i === 0
              ? <PrimaryButton key={a} className="min-h-14 text-base" onClick={() => setAction(a)}>{label[a]}</PrimaryButton>
              : <GhostButton key={a} className={`min-h-12 ${a === 'write_off' ? 'text-red-600 dark:text-red-400' : ''}`} onClick={() => setAction(a)}>{label[a]}</GhostButton>
          ))}
        </div>
      )}

      <section>
        <h2 className="mb-2 text-sm font-semibold">{t('history')}</h2>
        <ol className="space-y-2">
          {data.history.map((m) => <HistoryItem key={m.id} m={m} />)}
        </ol>
      </section>

      {action && <MoveDialog drum={d} action={action} title={label[action]} onClose={() => setAction(null)} />}
    </div>
  )
}

function Back() {
  const t = useT(inventoryMessages)
  return <Link to="/inventory" className="text-xs text-neutral-500 hover:underline">← {t('title')}</Link>
}

function Info({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-neutral-500">{label}</dt>
      <dd className="truncate">{value ?? '—'}</dd>
    </div>
  )
}

function HistoryItem({ m }: { m: Movement }) {
  const t = useT(inventoryMessages)
  const what = {
    receive: `${t('kindReceive')}: ${m.locationName ?? ''}`,
    transfer: `${t('kindTransfer')} ${m.locationName ?? ''}`,
    issue: `${t('kindIssue')} ${m.kolonaName ?? ''}`,
    return: `${t('kindReturn')} ${m.locationName ?? ''}`,
    count: t('kindCount'),
    write_off: `${t('kindWriteOff')} — ${m.reason === 'empty' ? t('reasonEmpty') : m.reason === 'damaged' ? t('reasonDamaged') : t('reasonLost')}`,
  }[m.kind]
  return (
    <li className="rounded-xl bg-white p-3 text-sm ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium">{what}</span>
        <span className="tabular shrink-0 text-xs">{fmtNumber(m.remainingM)} m{m.isEstimate && ` (${t('estimate')})`}</span>
      </div>
      <div className="mt-0.5 text-xs text-neutral-500">
        {fmtDateTime(m.occurredAt)}{m.actorName && ` · ${t('by', { name: m.actorName })}`}
        {m.projectName && ` · ${m.projectName}`}
        {m.meterMarkM != null && ` · ${t('markReading', { mark: fmtNumber(m.meterMarkM, 1) })}`}
      </div>
      {m.note && <div className="mt-1 text-xs italic text-neutral-600 dark:text-neutral-300">{m.note}</div>}
    </li>
  )
}

function useErrorText() {
  const t = useT(inventoryMessages)
  return (e: unknown) => t('couldNotSave', { error: e instanceof ApiError ? e.message : String(e) })
}

/** First scan of a label on a real drum. */
function RegisterForm({ drum }: { drum: Drum }) {
  const t = useT(inventoryMessages)
  const qc = useQueryClient()
  const errorText = useErrorText()
  const cables = useQuery<CableChoice[]>({ queryKey: ['inv-cables'], queryFn: () => api.get<CableChoice[]>('/api/inventory/cables'), staleTime: 10 * 60_000 })
  const locations = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: () => api.get<Location[]>('/api/inventory/locations') })
  const [cable, setCable] = useState('')
  const [length, setLength] = useState('')
  const [inner, setInner] = useState('')
  const [ref, setRef] = useState('')
  const [location, setLocation] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const active = (locations.data ?? []).filter((l) => l.active)
  const where = location || active.find((l) => l.kind === 'central')?.id || ''
  const chosen = cables.data?.find((c) => c.cableKey === cable)
  const metres = num(length)
  const valid = Boolean(chosen) && metres != null && metres > 0 && Boolean(where) && (inner.trim() === '' || num(inner) != null)

  const save = useMutation({
    mutationFn: () => api.post(`/api/inventory/drums/${drum.code}/register`, {
      cableKey: chosen!.cableKey, cableName: chosen!.name, providerId: chosen!.providerId, lengthM: metres,
      innerMarkM: inner.trim() === '' ? null : num(inner), supplierRef: ref.trim() || null, locationId: where, note: note.trim() || null,
    }),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ['inv-drum', drum.code] }), qc.invalidateQueries({ queryKey: ['inv-drums'] })]),
    onError: (e) => setError(errorText(e)),
  })

  return (
    <div className="space-y-4 rounded-2xl bg-white p-4 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
      <div>
        <h2 className="text-lg font-semibold">{t('unregisteredTitle')}</h2>
        <p className="text-sm text-neutral-500">{t('unregisteredHint')}</p>
      </div>
      <Field label={t('cable')}>
        <Select value={cable} onChange={setCable} placeholder={t('searchCable')}
          options={(cables.data ?? []).map((c) => ({ value: c.cableKey, label: c.provider ? `${c.name} — ${c.provider}` : c.name }))} />
      </Field>
      <Field label={t('lengthDelivered')}>
        <div className="flex items-center gap-2"><input inputMode="decimal" value={length} onChange={(e) => setLength(e.target.value)} className={`${INPUT} max-w-[10rem]`} /><span>m</span></div>
      </Field>
      <Field label={t('innerMark')} hint={t('innerMarkHint')}>
        <div className="flex items-center gap-2"><input inputMode="decimal" value={inner} onChange={(e) => setInner(e.target.value)} className={`${INPUT} max-w-[10rem]`} /><span>m</span></div>
      </Field>
      <Field label={t('receivedAt')}>
        <Select value={where} onChange={setLocation}
          options={active.map((l) => ({ value: l.id, label: l.name }))} />
      </Field>
      <Field label={t('supplierRef')}><input value={ref} onChange={(e) => setRef(e.target.value)} maxLength={200} className={INPUT} /></Field>
      <Field label={t('note')}><input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} className={INPUT} /></Field>
      {error && <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{error}</p>}
      <PrimaryButton className="min-h-14 w-full text-base" disabled={!valid || save.isPending} onClick={() => save.mutate()}>{t('register')}</PrimaryButton>
    </div>
  )
}

const num = (s: string): number | null => {
  const v = Number(s.trim().replace(',', '.'))
  return s.trim() !== '' && Number.isFinite(v) ? v : null
}

type Metres = { mode: 'keep' } | { mode: 'mark'; value: string } | { mode: 'estimate'; value: number } | { mode: 'metres'; value: string }

/** One handover: where to, for whom, and how much is left — read, estimated or typed. */
function MoveDialog({ drum, action, title, onClose }: { drum: Drum; action: Action; title: string; onClose: () => void }) {
  const t = useT(inventoryMessages)
  const qc = useQueryClient()
  const errorText = useErrorText()
  const needsPlace = action === 'transfer' || action === 'return'
  const locations = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: () => api.get<Location[]>('/api/inventory/locations'), enabled: needsPlace })
  const projects = useQuery<ProjectChoice[]>({ queryKey: ['inv-projects'], queryFn: () => api.get<ProjectChoice[]>('/api/inventory/projects'), staleTime: 10 * 60_000, enabled: action !== 'count' && action !== 'write_off' })
  const crews = useQuery<CrewChoice[]>({ queryKey: ['inv-crews'], queryFn: () => api.get<CrewChoice[]>('/api/inventory/crews'), staleTime: 10 * 60_000, enabled: action === 'issue' })

  const places = useMemo(() => (locations.data ?? []).filter((l) => l.active && l.id !== (action === 'transfer' ? drum.locationId : ''))
    .sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name) : action === 'transfer' ? (a.kind === 'site' ? -1 : 1) : 0)), [locations.data, action, drum.locationId])
  // Returning is usually back to the store the crew took it from.
  const [to, setTo] = useState(action === 'return' ? drum.locationId ?? '' : '')
  const place = places.find((l) => l.id === to)
  const [project, setProject] = useState(drum.projectId ?? '')
  const [crew, setCrew] = useState('')
  const [reason, setReason] = useState<'empty' | 'damaged' | 'lost' | ''>('')
  const [note, setNote] = useState('')
  const [metres, setMetres] = useState<Metres>(action === 'transfer' ? { mode: 'keep' } : drum.innerMarkM != null ? { mode: 'mark', value: '' } : { mode: 'estimate', value: -1 })
  const [error, setError] = useState<string | null>(null)

  // A site store's own projects first, then the rest.
  const projectList = useMemo(() => {
    const own = new Set(place?.projectIds ?? [])
    return [...(projects.data ?? [])].sort((a, b) => Number(own.has(b.projectId)) - Number(own.has(a.projectId)) || (a.name ?? '').localeCompare(b.name ?? ''))
  }, [projects.data, place])
  const crewList = crews.data ?? []

  const reading: Record<string, unknown> | null = (() => {
    if (action === 'write_off' && reason === 'empty') return {}
    switch (metres.mode) {
      case 'keep': return {}
      case 'mark': return num(metres.value) == null ? null : { meterMarkM: num(metres.value) }
      case 'estimate': return metres.value < 0 ? null : { estimateFraction: metres.value }
      case 'metres': return num(metres.value) == null ? null : { remainingM: num(metres.value) }
    }
  })()
  const valid = reading != null
    && (!needsPlace || Boolean(to))
    && (action !== 'issue' || Boolean(crew))
    && (action !== 'write_off' || Boolean(reason))

  const save = useMutation({
    mutationFn: () => api.post(`/api/inventory/drums/${drum.code}/movements`, {
      kind: action, locationId: needsPlace ? to : null,
      projectId: action === 'count' || action === 'write_off' || place?.kind === 'central' ? null : project || null,
      kolonaId: action === 'issue' ? crew : null,
      kolonaName: action === 'issue' ? crews.data?.find((c) => c.kolonaId === crew)?.name ?? null : null,
      reason: action === 'write_off' ? reason : null, note: note.trim() || null, ...reading,
    }),
    onSuccess: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: ['inv-drum', drum.code] }), qc.invalidateQueries({ queryKey: ['inv-drums'] }),
        qc.invalidateQueries({ queryKey: ['inv-locations'] })])
      onClose()
    },
    onError: (e) => setError(errorText(e)),
  })

  const fractions: [number, string][] = [[1, t('full')], [0.75, t('threeQuarters')], [0.5, t('half')], [0.25, t('quarter')], [0, t('empty')]]
  const chip = (on: boolean) => `min-h-11 flex-1 rounded-xl text-sm font-medium ring-1 ${on ? 'bg-brand-600 text-white ring-brand-600' : 'ring-neutral-300 dark:ring-neutral-700'}`

  return (
    <Dialog title={title} hint={`${drum.code} · ${drum.cableName ?? ''}`} onClose={onClose}
      footer={<><GhostButton onClick={onClose}>{t('cancel')}</GhostButton>
        <PrimaryButton disabled={!valid || save.isPending} onClick={() => save.mutate()}>{t('save')}</PrimaryButton></>}>
      {needsPlace && (
        <Field label={t('to')} hint={action === 'transfer' ? t('toSiteHint') : undefined}>
          <Select value={to} onChange={setTo} placeholder="—"
            options={places.map((l) => ({ value: l.id, label: `${l.name} · ${l.kind === 'central' ? t('kindCentral') : t('kindSite')}` }))} />
        </Field>
      )}
      {(action === 'transfer' || action === 'issue' || (action === 'return' && place?.kind === 'site')) && (
        <Field label={t('forProject')}>
          <Select value={project} onChange={setProject} empty={{ value: '', label: '—' }}
            options={projectList.map((p) => ({ value: p.projectId, label: `${p.name ?? '—'}${p.codeInternal ? ` · ${p.codeInternal}` : ''}` }))} />
        </Field>
      )}
      {action === 'issue' && (
        <Field label={t('crew')}>
          <Select value={crew} onChange={setCrew} placeholder={t('searchCrew')}
            options={crewList.map((c) => ({ value: c.kolonaId, label: c.name }))} />
        </Field>
      )}
      {action === 'write_off' && (
        <Field label={t('reason')}>
          <div className="flex gap-2">
            {(['empty', 'damaged', 'lost'] as const).map((r) => (
              <button key={r} type="button" className={chip(reason === r)} onClick={() => setReason(r)}>
                {r === 'empty' ? t('reasonEmpty') : r === 'damaged' ? t('reasonDamaged') : t('reasonLost')}
              </button>
            ))}
          </div>
        </Field>
      )}
      {!(action === 'write_off' && reason === 'empty') && (
        <Field label={t('metresLeft')}>
          <div className="flex gap-1 rounded-xl bg-neutral-100 p-1 dark:bg-neutral-800">
            {action === 'transfer' && <ModeTab on={metres.mode === 'keep'} onClick={() => setMetres({ mode: 'keep' })}>=</ModeTab>}
            <ModeTab on={metres.mode === 'mark'} onClick={() => setMetres({ mode: 'mark', value: '' })}>{t('byMark')}</ModeTab>
            <ModeTab on={metres.mode === 'estimate'} onClick={() => setMetres({ mode: 'estimate', value: -1 })}>{t('byEstimate')}</ModeTab>
            <ModeTab on={metres.mode === 'metres'} onClick={() => setMetres({ mode: 'metres', value: '' })}>{t('byMetres')}</ModeTab>
          </div>
          {metres.mode === 'keep' && <p className="text-sm text-neutral-600 dark:text-neutral-300">{t('keepMetres', { metres: fmtNumber(drum.remainingM ?? 0) })}</p>}
          {metres.mode === 'mark' && (drum.innerMarkM == null
            ? <p className="text-sm text-amber-700 dark:text-amber-300">{t('noInnerMark')}</p>
            : <><div className="flex items-center gap-2"><input inputMode="decimal" autoFocus value={metres.value} onChange={(e) => setMetres({ mode: 'mark', value: e.target.value })} className={`${INPUT} max-w-[10rem] text-lg`} /><span>m</span></div>
                <span className="block text-xs text-neutral-500">{t('markHint')}</span></>)}
          {metres.mode === 'estimate' && (
            <div className="flex gap-1.5">
              {fractions.map(([v, l]) => <button key={v} type="button" className={chip(metres.value === v)} onClick={() => setMetres({ mode: 'estimate', value: v })}>{l}</button>)}
            </div>
          )}
          {metres.mode === 'metres' && (
            <div className="flex items-center gap-2"><input inputMode="decimal" autoFocus value={metres.value} onChange={(e) => setMetres({ mode: 'metres', value: e.target.value })} className={`${INPUT} max-w-[10rem] text-lg`} /><span>m</span></div>
          )}
        </Field>
      )}
      <Field label={t('note')}><input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} className={INPUT} /></Field>
      {error && <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{error}</p>}
    </Dialog>
  )
}

function ModeTab({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick}
      className={`min-h-10 flex-1 rounded-lg text-sm font-medium ${on ? 'bg-white shadow-sm dark:bg-neutral-900' : 'text-neutral-500'}`}>
      {children}
    </button>
  )
}
